/**
 * I18n Helper for JavaScript Controllers
 *
 * Provides utilities for accessing internationalized messages stored in data-i18n-* attributes.
 * Works with Rails I18n translations passed to HTML data attributes.
 *
 * Environment-aware error handling:
 * - Development: Throws errors for missing translations (fail fast)
 * - Production: Logs warnings + sends to monitoring service (graceful degradation)
 *
 * @module i18n_helper
 *
 * @example
 * // In ERB template:
 * <div data-controller="product-form"
 *      data-i18n-loading="<%= t('javascript.product_form.loading') %>"
 *      data-i18n-error="<%= t('javascript.product_form.error') %>">
 *
 * // In Stimulus controller:
 * import { getI18nMessage } from '../lib/i18n_helper'
 *
 * connect() {
 *   const loadingMsg = getI18nMessage(this.element, 'loading', 'Loading...')
 *   console.log(loadingMsg)
 * }
 */

/**
 * Detect Rails environment from meta tag
 * @returns {string} 'development', 'test', or 'production'
 */
function getRailsEnvironment() {
  const meta = document.querySelector('meta[name="rails-env"]')
  return meta ? meta.content : 'production'
}

/**
 * Send error to monitoring service (Sentry, etc.)
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
function sendToMonitoring(error, context = {}) {
  // Check if Sentry is available
  if (typeof window.Sentry !== 'undefined') {
    window.Sentry.captureException(error, {
      tags: { component: 'i18n_helper' },
      extra: context
    })
  }

  // Fallback: Send to custom error tracking endpoint if available
  if (typeof window.ErrorTracker !== 'undefined') {
    window.ErrorTracker.captureException(error, context)
  }
}

/**
 * Get internationalized message from data-i18n-* attribute with optional interpolation
 *
 * @param {HTMLElement} element - Controller element with data-i18n-* attributes
 * @param {string} key - Message key (e.g., 'loading', 'error', 'success')
 * @param {string} fallback - Fallback message if translation not found
 * @param {Object} interpolations - Key-value pairs for placeholder replacement
 * @returns {string} Translated message with interpolated values or fallback
 *
 * @example
 * // Simple usage
 * const message = getI18nMessage(this.element, 'loading', 'Loading...')
 *
 * @example
 * // With interpolation
 * // YAML: description_remaining: "還可輸入 %{count} 字"
 * const message = getI18nMessage(this.element, 'descriptionRemaining', '', { count: 100 })
 * // Returns: "還可輸入 100 字"
 */
export function getI18nMessage(element, key, fallback = '', interpolations = {}) {
  const attributeName = `i18n${key.charAt(0).toUpperCase()}${key.slice(1)}`
  let message = element.dataset[attributeName]
  const env = getRailsEnvironment()

  // Handle missing translation
  if (!message) {
    // Gather debugging information
    const availableI18nAttrs = Object.keys(element.dataset)
      .filter(k => k.startsWith('i18n'))
      .map(k => k.replace(/^i18n/, '').replace(/^(.)/, m => m.toLowerCase()))
      .join(', ') || 'none'

    const expectedAttribute = `data-${attributeName.replace(/([A-Z])/g, '-$1').toLowerCase()}`
    const controllerName = element.dataset.controller || 'unknown'

    const errorDetails = [
      `[I18n] Missing translation for key: ${key}`,
      `Expected attribute: ${expectedAttribute}`,
      `Controller: ${controllerName}`,
      `Available I18n keys: ${availableI18nAttrs}`,
      `Fallback: "${fallback}"`
    ].join('\n  ')

    const error = new Error(errorDetails)

    if (env === 'development') {
      // Development: Fail fast with descriptive error
      console.error(errorDetails)
      throw error
    } else {
      // Production: Log warning + send to monitoring
      console.warn(errorDetails)
      sendToMonitoring(error, {
        key,
        fallback,
        attributeName,
        expectedAttribute,
        availableI18nKeys: availableI18nAttrs,
        controllerName
      })
    }

    return fallback
  }

  // Handle interpolations: %{key} → value
  Object.keys(interpolations).forEach(interpolationKey => {
    const value = interpolations[interpolationKey]
    const regex = new RegExp(`%\\{${interpolationKey}\\}`, 'g')
    message = message.replace(regex, value !== undefined && value !== null ? value : '')
  })

  // Check for remaining uninterpolated placeholders
  const remainingPlaceholders = message.match(/%\{[^}]+\}/g)
  if (remainingPlaceholders) {
    const error = new Error(
      `[I18n] Uninterpolated placeholders in key "${key}": ${remainingPlaceholders.join(', ')}`
    )

    if (env === 'development') {
      // Development: Fail fast
      console.error(error.message)
      throw error
    } else {
      // Production: Log warning + send to monitoring
      console.warn(error.message)
      sendToMonitoring(error, {
        key,
        message,
        remainingPlaceholders,
        providedInterpolations: Object.keys(interpolations),
        controllerName: element.dataset.controller || 'unknown'
      })
    }
  }

  return message
}

/**
 * Get multiple I18n messages at once with environment-aware error handling
 *
 * @param {HTMLElement} element - Controller element with data-i18n-* attributes
 * @param {string[]} keys - Array of message keys
 * @returns {Object} Object mapping keys to translated messages
 *
 * @example
 * const messages = getI18nMessages(this.element, ['loading', 'error', 'success'])
 * // Returns: { loading: '載入中...', error: '錯誤', success: '成功' }
 */
export function getI18nMessages(element, keys) {
  const messages = {}
  const env = getRailsEnvironment()
  const missingKeys = []

  keys.forEach(key => {
    const attributeName = `i18n${key.charAt(0).toUpperCase()}${key.slice(1)}`
    messages[key] = element.dataset[attributeName] || ''

    if (!messages[key]) {
      missingKeys.push(key)
    }
  })

  // Handle missing translations
  if (missingKeys.length > 0) {
    const error = new Error(
      `[I18n] Missing translations for keys: ${missingKeys.join(', ')}`
    )

    if (env === 'development') {
      // Development: Fail fast
      console.error(error.message)
      throw error
    } else {
      // Production: Log warning + send to monitoring
      console.warn(error.message)
      sendToMonitoring(error, {
        missingKeys,
        requestedKeys: keys,
        controllerName: element.dataset.controller || 'unknown'
      })
    }
  }

  return messages
}

/**
 * Check if I18n message exists for given key
 *
 * @param {HTMLElement} element - Controller element with data-i18n-* attributes
 * @param {string} key - Message key to check
 * @returns {boolean} True if message exists
 *
 * @example
 * if (hasI18nMessage(this.element, 'customError')) {
 *   const msg = getI18nMessage(this.element, 'customError')
 * }
 */
export function hasI18nMessage(element, key) {
  const attributeName = `i18n${key.charAt(0).toUpperCase()}${key.slice(1)}`
  return attributeName in element.dataset && element.dataset[attributeName] !== ''
}

/**
 * Get all I18n messages from element
 *
 * @param {HTMLElement} element - Controller element with data-i18n-* attributes
 * @returns {Object} Object containing all i18n messages
 *
 * @example
 * const allMessages = getAllI18nMessages(this.element)
 * // Returns: { loading: '載入中...', error: '錯誤', ... }
 */
export function getAllI18nMessages(element) {
  const messages = {}
  const dataset = element.dataset

  Object.keys(dataset).forEach(key => {
    if (key.startsWith('i18n')) {
      // Convert 'i18nLoading' to 'loading'
      const messageKey = key.replace('i18n', '').charAt(0).toLowerCase() + key.slice(5)
      messages[messageKey] = dataset[key]
    }
  })

  return messages
}

/**
 * Create I18n getter for Stimulus controller
 * Returns a function bound to controller's element with interpolation support
 *
 * @param {Controller} controller - Stimulus controller instance
 * @returns {Function} Bound getI18nMessage function
 *
 * @example
 * // In Stimulus controller connect():
 * import { createI18nGetter } from '../lib/i18n_helper'
 *
 * connect() {
 *   this.t = createI18nGetter(this)
 *   console.log(this.t('loading', 'Loading...'))
 *   console.log(this.t('remaining', '', { count: 100 }))
 * }
 */
export function createI18nGetter(controller) {
  return (key, fallback = '', interpolations = {}) =>
    getI18nMessage(controller.element, key, fallback, interpolations)
}
